import React from 'react';
import { Intl, Lazy } from '@erecruit/core';
import {
  closeActionStatus,
  showActionStatus,
  getErrorInfo,
  transformError,
} from '@erecruit/modules';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { H1, H2, Flex, Button, styled, Icons, Icon } from '@erecruit/ui-kit';
import { LoadingInfo, ErrorInfo, SuccessInfo } from './StatusInfoComponents';

const { FormattedMessage } = Intl;

const ActionStatusModalHeight = 400;

type CustomActionInfo = {
  action: (history: Pick<RouteComponentProps, 'history'>['history']) => void;
  display: { text: Intl.IntlString; icon?: Icons };
};

interface StatusInfoProps<PayloadType, G1, G2> {
  successGeneratorFn: (payload: PayloadType) => Generator<G1, void, unknown>;
  errorGeneratorFn: (e: Error) => Generator<G2, void, unknown>;
  getHeader: (
    payload: PayloadType,
  ) => { title: Intl.IntlString; subtitle?: Intl.IntlString };
  getSuccessMessage: (payload: PayloadType) => Intl.IntlString;
  getLoadingMessage: (payload: PayloadType) => Intl.IntlString;
  autocloseMs?: number;
  getCustomErrorAction?: (payload: PayloadType) => CustomActionInfo;
  getCustomSuccessAction?: (payload: PayloadType) => CustomActionInfo;
}

const Title = styled(H1)({
  marginBottom: 0,
});

const Subtitle = styled(H2)({
  opacity: 0.4,
});

const showStatus = ({
  status,
  message,
  messageId,
  values,
  additionalNode,
}: {
  status: React.ReactNode;
  message?: string;
  messageId?: Intl.LocalizationKeysArray;
  values?: { [key: string]: string };
  additionalNode?: Lazy<React.ReactNode>;
}) => (
  <Flex direction="column" align="center" justify="center" stretch>
    {status}
    <H2 padding={[6, 0]} weight={400} align="center">
      {message || null}
      {messageId && <FormattedMessage id={messageId} values={values} />}
    </H2>
    {additionalNode && additionalNode()}
  </Flex>
);

function CustomAction({ actionInfo }: { actionInfo: CustomActionInfo }) {
  const history = useHistory();
  const { text, icon } = actionInfo.display;
  return (
    <Button type="primary" onClick={() => actionInfo.action(history)}>
      <FormattedMessage id={text.id} values={text.values} />
      {icon && <Icon type={icon} textual />}
    </Button>
  );
}

export const withStatusInfo = <PayloadType, G1, G2>({
  successGeneratorFn,
  errorGeneratorFn,
  getSuccessMessage,
  getLoadingMessage,
  getHeader,
  autocloseMs,
  getCustomSuccessAction,
  getCustomErrorAction,
}: StatusInfoProps<PayloadType, G1, G2>) => {
  const delay = (ms: number) => new Promise(res => setTimeout(res, ms));
  return function*(payload: PayloadType) {
    const headerInfo = getHeader(payload);
    const header = () => (
      <Flex direction="column" align="center">
        <Title color="headerTertiary">
          <FormattedMessage
            id={headerInfo.title.id}
            values={headerInfo.title.values}
          />
        </Title>
        {headerInfo.subtitle && (
          <Subtitle color="headerTertiary" align="center">
            <FormattedMessage
              id={headerInfo.subtitle.id}
              values={headerInfo.subtitle.values}
            />
          </Subtitle>
        )}
      </Flex>
    );

    const loadingMessage = getLoadingMessage(payload);
    yield showActionStatus({
      content: () =>
        showStatus({
          status: <LoadingInfo />,
          messageId: loadingMessage.id,
          values: loadingMessage.values,
        }),
      header: header,
      height: ActionStatusModalHeight,
    });

    try {
      yield successGeneratorFn(payload);

      let Action = () => <></>;
      if (getCustomSuccessAction) {
        const actionInfo = getCustomSuccessAction(payload);
        Action = () => <CustomAction actionInfo={actionInfo} />;
      }

      const successMessage = getSuccessMessage(payload);
      yield showActionStatus({
        content: () =>
          showStatus({
            status: <SuccessInfo />,
            messageId: successMessage.id,
            values: successMessage.values,
            additionalNode: Action,
          }),
        header: header,
        height: ActionStatusModalHeight,
      });

      if (autocloseMs && !getCustomSuccessAction) {
        yield delay(autocloseMs);
        yield closeActionStatus();
      }
    } catch (e) {
      yield errorGeneratorFn(e);

      const errorMessage = getErrorInfo(transformError(e));

      let Action = () => <></>;
      if (getCustomErrorAction) {
        const actionInfo = getCustomErrorAction(payload);
        Action = () => <CustomAction actionInfo={actionInfo} />;
      }

      yield showActionStatus({
        content: () =>
          showStatus({
            status: <ErrorInfo />,
            message: errorMessage.message,
            additionalNode: Action,
          }),
        header: header,
        height: ActionStatusModalHeight,
      });
    }
  };
};
